import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Loader2, Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("elm_admin_token") || ""}` }, withCredentials: true });
const errMsg = (e) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
const input = "bg-white/60 border border-wood/25 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-terra";
const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const Creneaux = ({ label, value, onChange, testid }) => {
  const [h, setH] = useState("");
  return (
    <div>
      <p className="eyebrow mb-2">{label}</p>
      <div className="flex flex-wrap gap-2 items-center">
        {value.map((c) => (
          <span key={c} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-wood/25 text-xs font-mono text-bark" data-testid={`${testid}-${c.replace(":", "")}`}>
            {c}<button type="button" aria-label={`Retirer ${c}`} onClick={() => onChange(value.filter((x) => x !== c))} className="text-fumee hover:text-terra"><Trash2 className="w-3 h-3" /></button>
          </span>
        ))}
        <input type="time" value={h} onChange={(e) => setH(e.target.value)} className={`${input} w-28`} data-testid={`${testid}-input`} />
        <button type="button" disabled={!h} onClick={() => { onChange([...new Set([...value, h])].sort()); setH(""); }} className="p-2 rounded-full border border-wood/25 hover:border-terra disabled:opacity-40" data-testid={`${testid}-ajouter`}><Plus className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
};

export const AdminDisponibilites = () => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/disponibilites`, authHeaders()).then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!cfg) return <Loader2 className="w-5 h-5 animate-spin text-fumee" />;
  const set = (k) => (v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { const r = await axios.put(`${API}/admin/disponibilites`, cfg, authHeaders()); setCfg(r.data); toast.success("Plages de travail enregistrées"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-card border border-sand-dark rounded-2xl p-5 space-y-6" data-testid="admin-dispo">
      <div className="flex items-start gap-4">
        <Clock className="w-5 h-5 mt-0.5 text-terra" />
        <div className="text-sm text-bark">
          <p className="font-serif text-lg">Plages de travail & règles de réservation</p>
          <p className="text-xs text-fumee mt-1">Seuls ces créneaux sont proposés sur le site, moins vos événements, vos rendez-vous et vos occupations Google Agenda. Fuseau Europe/Zurich.</p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Creneaux label="Consultations — heures de début" value={cfg.consult_creneaux} onChange={set("consult_creneaux")} testid="admin-dispo-consult" />
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-fumee">Battement (min)<input type="number" min={0} max={180} value={cfg.consult_ecart_min} onChange={(e) => set("consult_ecart_min")(+e.target.value)} className={`${input} w-full mt-1`} data-testid="admin-dispo-consult-ecart" /></label>
            <label className="text-xs text-fumee">Délai mini (jours)<input type="number" min={0} max={60} value={cfg.consult_delai_jours} onChange={(e) => set("consult_delai_jours")(+e.target.value)} className={`${input} w-full mt-1`} data-testid="admin-dispo-consult-delai" /></label>
          </div>
        </div>
        <div className="space-y-4">
          <Creneaux label="Visites de lieux — heures de début" value={cfg.lieu_creneaux} onChange={set("lieu_creneaux")} testid="admin-dispo-lieu" />
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-fumee">Battement (h)<input type="number" min={0} max={6} value={cfg.lieu_ecart_h} onChange={(e) => set("lieu_ecart_h")(+e.target.value)} className={`${input} w-full mt-1`} data-testid="admin-dispo-lieu-ecart" /></label>
            <label className="text-xs text-fumee">Délai mini (jours)<input type="number" min={0} max={60} value={cfg.lieu_delai_jours} onChange={(e) => set("lieu_delai_jours")(+e.target.value)} className={`${input} w-full mt-1`} data-testid="admin-dispo-lieu-delai" /></label>
          </div>
        </div>
      </div>
      <div>
        <p className="eyebrow mb-2">Jours fermés</p>
        <div className="flex flex-wrap gap-2">
          {JOURS.map((j, i) => {
            const on = cfg.jours_fermes.includes(i);
            return <button type="button" key={j} onClick={() => set("jours_fermes")(on ? cfg.jours_fermes.filter((x) => x !== i) : [...cfg.jours_fermes, i])} data-testid={`admin-dispo-jour-${i}`} className={`px-3 py-1.5 rounded-full border text-xs ${on ? "border-terra bg-terra/10 text-terra line-through" : "border-wood/25 text-bark"}`}>{j}</button>;
          })}
        </div>
      </div>
      <div>
        <p className="eyebrow mb-2">Fermetures (vacances, absences)</p>
        <div className="space-y-2">
          {cfg.fermetures.map((f, i) => (
            <div key={i} className="flex flex-wrap gap-2 items-center" data-testid={`admin-dispo-fermeture-${i}`}>
              <input type="date" value={f.du} onChange={(e) => set("fermetures")(cfg.fermetures.map((x, k) => (k === i ? { ...x, du: e.target.value } : x)))} className={input} />
              <span className="text-xs text-fumee">→</span>
              <input type="date" value={f.au} onChange={(e) => set("fermetures")(cfg.fermetures.map((x, k) => (k === i ? { ...x, au: e.target.value } : x)))} className={input} />
              <input placeholder="Motif (privé)" value={f.motif || ""} onChange={(e) => set("fermetures")(cfg.fermetures.map((x, k) => (k === i ? { ...x, motif: e.target.value } : x)))} className={`${input} flex-1 min-w-[8rem]`} />
              <button type="button" onClick={() => set("fermetures")(cfg.fermetures.filter((_, k) => k !== i))} className="p-2 text-fumee hover:text-terra" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button type="button" onClick={() => set("fermetures")([...cfg.fermetures, { du: "", au: "", motif: "" }])} data-testid="admin-dispo-fermeture-ajouter" className="inline-flex items-center gap-1.5 text-xs text-bark hover:text-terra"><Plus className="w-3.5 h-3.5" /> Ajouter une période</button>
        </div>
      </div>
      <button type="button" disabled={busy} onClick={save} data-testid="admin-dispo-enregistrer" className="px-5 py-2.5 rounded-full bg-earth text-sand text-sm hover:bg-terra hover:text-earth transition-colors disabled:opacity-50">{busy ? "Enregistrement…" : "Enregistrer"}</button>
    </div>
  );
};
